import { Logger, NcwOnboardingRecoveryImage, pasteTextFromClipboard, queryClient } from '@capsicum/common';
import { Alert, Image, SubmitButton, Text, TextInput } from '@capsicum/common-web';
import { InfoIcon, PasteIcon } from '@capsicum/common-web/icons';
import { useNCWalletRecovery } from '@capsicum/features/nc-onboarding/web';
import { zodResolver } from '@hookform/resolvers/zod';
import { RecoveryError, RecoveryErrorKind, useNCWRecovery } from '@socios-blk/browser';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';

import { useNCWalletRecoverySuccessDrawer } from '../../hooks';
import { RecoveryErrorDrawer } from '../../ui';

export type NCWalletRecoveryFormProps = {
	onCompletedRecovery: (newRecoveryKit: { credentialId: string; secret: string }) => void;
};

export const NCWalletRecoveryForm = ({ onCompletedRecovery }: NCWalletRecoveryFormProps) => {
	const { openWalletRecoverSuccessDrawer } = useNCWalletRecoverySuccessDrawer();
	const { error, formSchema, intlTexts, setError } = useNCWalletRecovery();

	const { recoverWallet } = useNCWRecovery();

	const formMethods = useForm({
		defaultValues: {
			recoveryCode: '',
			recoveryKitId: ''
		},
		mode: 'onChange',
		resolver: zodResolver(formSchema)
	});

	const handleSubmit = async () => {
		try {
			const result = await recoverWallet(formMethods.getValues());

			if (!result?.newRecoveryKit) {
				throw new Error('recoverWallet did not produce a new recovery kit, cannot proceed');
			}

			/**
			 * This is a work around. Both NCWalletRecoveryForm and RecoveryKitGenerated render twice due to their
			 * parent renders twice; therefore, I couldn't call openWalletRecoverSuccessDrawer in the components'
			 * mounting and unmounting life cycles
			 */
			openWalletRecoverSuccessDrawer();
			onCompletedRecovery(result.newRecoveryKit);

			queryClient.invalidateQueries({ queryKey: ['nonCustodialProviderManagementAuthActionLogin'] });
		} catch (error) {
			if (error instanceof RecoveryError) {
				if (error.kind === RecoveryErrorKind.endpointErrorOrNetworkFailure) {
					Logger.error('Error during wallet recovery', { error });
				}

				setError(error.kind);

				return;
			}

			throw error;
		}
	};

	const paste = async (fieldName: 'recoveryCode' | 'recoveryKitId') => {
		try {
			formMethods.setValue(fieldName, await pasteTextFromClipboard());
			await formMethods.trigger(fieldName);
		} catch (_e) {
			// Browsers sometimes don't allow pasting and throw instead
		}
	};

	return (
		<>
			{error && <RecoveryErrorDrawer error={error} setError={setError} />}
			<S.Container hidden={!!error}>
				<S.Wrapper>
					<S.StyledImage alt="Recovery" height={132} src={NcwOnboardingRecoveryImage} />
					<S.TitleText variant="title3">{intlTexts.title}</S.TitleText>
					<S.Explanation variant="text3">{intlTexts.explanation}</S.Explanation>
					<FormProvider {...formMethods}>
						<S.Form onSubmit={formMethods.handleSubmit(handleSubmit)}>
							<TextInput
								autoCapitalize="none"
								autoComplete="false"
								iconEnd={{
									icon: PasteIcon,
									onClick: () => paste('recoveryCode')
								}}
								label={intlTexts.inputRecoveryCode}
								name="recoveryCode"
								testID="walletRecovery-form-recoveryCode-input"
							/>
							<TextInput
								autoCapitalize="none"
								autoComplete="false"
								iconEnd={{
									icon: PasteIcon,
									onClick: () => paste('recoveryKitId')
								}}
								label={intlTexts.inputRecoveryKeyId}
								name="recoveryKitId"
								testID="walletRecovery-form-recoveryKitId-input"
							/>
							<Alert closable={false} icon={InfoIcon} testID="walletRecovery-alert" type="message">
								{intlTexts.info}
							</Alert>
							<SubmitButton testID="walletRecovery-form-submit-button" text={intlTexts.submitForm} />
						</S.Form>
					</FormProvider>
				</S.Wrapper>
			</S.Container>
		</>
	);
};

namespace S {
	export const Container = styled.div`
		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			margin: ${({ theme }) => theme.spacings.small};
		}
		margin-top: ${({ theme }) => theme.spacings.medium};
		width: 100%;
	`;
	export const Form = styled.form`
		display: contents;
	`;

	export const StyledImage = styled(Image)`
		width: 100%;
		object-fit: contain;
		box-sizing: content-box;
		margin-block: ${({ theme }) => theme.spacings.xlarge};
	`;

	export const TitleText = styled(Text)`
		text-align: center;
	`;

	export const Explanation = styled(Text)`
		text-align: center;
		margin-bottom: ${({ theme }) => theme.spacings.medium};
	`;

	export const Wrapper = styled.div`
		width: 100%;
		display: flex;
		flex: 1;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.small};
		padding: ${({ theme }) => theme.spacings.small};
		border-radius: ${({ theme }) => theme.sizing.xxsmall};
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
	`;
}
